import { supabase } from "../../lib/supabaseClient";
import { getAdminScopeUserId } from "../../config/admin";
import { isManualPremiumValid, isUserPremium } from "../../util/premiumAccess";

function requireClient() {
  if (!supabase) throw new Error("Supabase is not configured.");
  return supabase;
}

export async function GetSubscriptionStatus(subscriptionId) {
  if (!subscriptionId) return { valid: false };

  const { data, error } = await requireClient()
    .from("profiles")
    .select(
      "subscription_active, stripe_subscription_id, subscription_source, premium_until"
    )
    .eq("stripe_subscription_id", subscriptionId)
    .maybeSingle();
  if (error) throw error;

  const valid =
    isUserPremium(data) && data?.stripe_subscription_id === subscriptionId;
  return { valid };
}

export async function GetPremiumStatusForUser(userId) {
  if (!userId) return { valid: false, stripeSubscriptionId: null, source: null };

  const { data, error } = await requireClient()
    .from("profiles")
    .select(
      "subscription_active, stripe_subscription_id, subscription_source, premium_until"
    )
    .eq("id", userId)
    .maybeSingle();
  if (error) throw error;

  return {
    valid: isUserPremium(data),
    stripeSubscriptionId: data?.stripe_subscription_id ?? null,
    source: data?.subscription_source ?? "stripe",
    premiumUntil: data?.premium_until ?? null,
  };
}

export async function invokeAdminUsers(action, payload = {}) {
  const client = requireClient();
  const base = process.env.REACT_APP_SUPABASE_URL?.replace(/\/$/, "") || "";
  const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || "";
  if (!base || !anonKey) {
    throw new Error("Supabase is not configured.");
  }

  // Prefer direct fetch so CORS/preflight failures surface clearly and large
  // listUsers payloads are not dropped by the functions.invoke helper.
  const {
    data: { session },
  } = await client.auth.getSession();
  const authToken = session?.access_token;
  if (!authToken) {
    throw new Error("Admin session required");
  }

  const response = await fetch(`${base}/functions/v1/admin-users`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
      apikey: anonKey,
    },
    body: JSON.stringify({ action, ...payload }),
  });

  const text = await response.text();
  let parsed = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = null;
  }

  if (!response.ok) {
    const message =
      (parsed && (parsed.error || parsed.message)) ||
      text ||
      `Admin request failed (${response.status})`;
    throw new Error(message);
  }

  return parsed;
}

export async function grantManualPremium(userId, { premiumUntil = null } = {}) {
  if (!userId) throw new Error("userId required");
  const adminId = getAdminScopeUserId();
  if (!adminId) throw new Error("Admin session required");
  return invokeAdminUsers("grantPremium", {
    adminId,
    userId: String(userId),
    premiumUntil: premiumUntil ?? null,
  });
}

/** Admin: revoke manual premium grant. */
export async function revokeManualPremium(userId) {
  if (!userId) throw new Error("userId required");
  const adminId = getAdminScopeUserId();
  if (!adminId) throw new Error("Admin session required");
  return invokeAdminUsers("revokePremium", {
    adminId,
    userId: String(userId),
  });
}
